import { useMemo } from "react";

import moment from "moment";

import {
  useGetOptionChainSnapshots,
  useGetOptionContracts,
  useGetOptionExpirations,
} from "~/v2/api/hooks";
import type { Asset } from "~/v2/api/rest";
import type {
  GetOptionSnapshotsResponse,
  OptionContract,
} from "~/v2/api/rest/data";

export interface OptionContractWithSnapshot extends OptionContract {
  snapshot?: GetOptionSnapshotsResponse["snapshots"][string];
}

export interface UseGetOptionsDataArgs {
  asset: Asset | undefined;
  expirationDate: string;
  isCrypto: boolean;
}

export const useGetOptionsData = ({
  asset,
  expirationDate,
  isCrypto,
}: UseGetOptionsDataArgs) => {
  const { data: expirationsRes } = useGetOptionExpirations({
    assetSymbol: asset?.symbol,
    isCrypto,
  });
  const optionExpirations = expirationsRes?.option_contracts ?? [];

  const expirations = useMemo(
    () =>
      Array.from(
        new Set(optionExpirations.map((entry) => entry.expiration_date)),
      ) // sort by date (descending)
        .sort((a, b) => moment(a).unix() - moment(b).unix()),
    [optionExpirations],
  );

  const {
    data: contractsRes,
    isFetchedAfterMount: isContractsFetchedAfterMount,
    isRefetching: isContractsRefetching,
  } = useGetOptionContracts({
    assetSymbol: asset?.symbol,
    expirationDate,
    options: {
      enabled: !isCrypto && !!asset && !!expirationDate,
    },
  });
  const optionContracts = contractsRes?.option_contracts ?? [];

  const { data: snapshotData } = useGetOptionChainSnapshots({
    underlyingSymbol: asset?.symbol,
    expirationDate,
    isCrypto,
  });

  const contracts: OptionContractWithSnapshot[] = useMemo(
    () =>
      optionContracts.map((contract) => ({
        ...contract,
        snapshot: snapshotData?.snapshots?.[contract.symbol],
      })),
    [optionContracts, snapshotData],
  );

  return {
    expirations,
    optionContracts: contracts,
    isContractsFetchedAfterMount,
    isContractsRefetching,
  };
};
